import axios from 'axios'
import { getScreenToken, forgetPairing } from './storage'

const BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000'

/** Absolute URL for a path the API returns relative to its host (ad images). */
export const apiAsset = (path: string) => `${BASE_URL}${path}`

export const apiClient = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 10_000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Attach screen JWT on every request
apiClient.interceptors.request.use((config) => {
  const token = getScreenToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// A 401 only comes from the screen-token routes (/screens/*): the token was
// revoked or expired, so go back to pairing. Network errors and 5xx keep
// retrying as before.
apiClient.interceptors.response.use(undefined, (error) => {
  const url: string = error?.config?.url ?? ''
  if (error?.response?.status === 401 && url.startsWith('/screens/') && url !== '/screens/pair' && getScreenToken()) {
    forgetPairing()
  }
  return Promise.reject(error)
})

// Typed response helpers
export const screenApi = {
  /** Pair this screen with a pairing code. Returns { token, screen } */
  pair: (pairingCode: string) =>
    apiClient.post<{ token: string; screen: { id: string; roomId: string; name: string; tenantId: string } }>(
      '/screens/pair',
      { pairingCode }
    ),

  /** Get the currently active session for a room */
  getActiveSession: (roomId: string) =>
    apiClient.get<{ id: string; status: string } | null>(
      `/sessions/room/${roomId}/active`
    ),

  /** Full session snapshot (leaderboard + roster) */
  getSnapshot: (sessionId: string) =>
    apiClient.get(`/sessions/${sessionId}/snapshot`),

  /** Next PRESTART session for a room (for split-screen awareness) */
  getNextUpSession: (roomId: string) =>
    apiClient.get(`/sessions/room/${roomId}/next-up`),

  /** Server-declared screen program — the authoritative phase for this
   *  room. See ScreenProgram in @gymboard/types. */
  getScreenProgram: (roomId: string) =>
    apiClient.get<import('@gymboard/types').ScreenProgram>(
      `/sessions/room/${roomId}/program`
    ),

  /** Upcoming scheduled classes for a room */
  // Screen-token endpoint scoped to this screen's room; /classes/instances
  // only accepts user logins, so the TV never saw its next class.
  getUpcomingClasses: (_roomId: string) => apiClient.get('/screens/me/upcoming'),

  /** Gym-wide leaderboard for the idle screen (this screen's gym only) */
  getGymLeaderboard: (
    metric: import('@gymboard/types').GymLeaderboardMetric,
    period: import('@gymboard/types').GymLeaderboardPeriod = 'week',
    limit = 8,
  ) =>
    apiClient.get<Omit<import('@gymboard/types').GymLeaderboard, 'me'>>('/screens/me/leaderboard', {
      params: { metric, period, limit },
    }),

  /** Which per-gym features are on for this screen's gym */
  getFeatures: () => apiClient.get<import('@gymboard/types').FeatureMap>('/screens/me/features'),

  /** Live ads for the idle screen (this screen's gym, TV placement only) */
  getAds: () => apiClient.get<import('@gymboard/types').PublicAd[]>('/screens/me/ads'),

  /** Count one showing of an ad on this screen */
  reportAdImpression: (adId: string) => apiClient.post(`/screens/me/ads/${adId}/impression`),

  /** Generate / refresh a QR token for athlete check-in */
  generateQr: (sessionId: string) =>
    apiClient.get<{ token: string; expiresAt: number }>(
      `/qr/session/${sessionId}`
    ),
}

export default apiClient
